import { Box, SelectChangeEvent } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import IpProjectIcon from "../../../assets/images/icons/IpProjectIcon";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlags.ts";
import RouterConstants from "../../../constants/RouterConstants";
import { useLdFeatureFlag } from "../../../hooks/useLdFeatureFlag.ts";
import useSecureNavigate from "../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../hooks/useStores";
import { capitalizeFirstLetter, isAppsURL, isHanukaOrgs } from "../../../utils";
import BriaDropdown from "../BriaDropDown/BriaDropDown";
import orgStyles from "../OrganizationsDropdown/OrganizationsDropdown.module.scss";
import dropdownStyles from "./IpProjectsDropdown.module.scss";

const IpProjectsDropdown = () => {
	const { ipProjectsStore } = useAppStore();
	const { t } = useTranslation("common");
	const [value, setValue] = useState<string>(ipProjectsStore.selectedIpProjectId ?? "");
	const loading = ipProjectsStore.loading;
	const navigate = useSecureNavigate();
	const isIpProjectsEnabled = useLdFeatureFlag(FEATURE_FLAGS.IP_PROJECTS_FEATURE) || isHanukaOrgs();

	// Keep selection in sync with store
	useEffect(() => {
		setValue(ipProjectsStore.selectedIpProjectId ?? "");
	}, [ipProjectsStore.selectedIpProjectId]);

	// Ensure ip projects are loaded when the dropdown mounts (e.g., direct load into demo pages)
	useEffect(() => {
		if (!ipProjectsStore.loading && ipProjectsStore.ipProjects.length === 0) {
			ipProjectsStore.loadIpProjects();
		}
	}, [ipProjectsStore]);

	useEffect(() => {
		if (!isIpProjectsEnabled) {
			ipProjectsStore.setSelectedIpProjectId(null);
		}
	}, [isIpProjectsEnabled]);

	const changeHandler = useCallback(
		(event: SelectChangeEvent<string>) => {
			const newVal = event.target.value;
			if (!newVal) {
				setValue("");
				ipProjectsStore.setSelectedIpProjectId(null);
				return;
			}
			setValue(newVal);
			ipProjectsStore.setSelectedIpProjectId(newVal);
		},
		[ipProjectsStore],
	);

	const items = ipProjectsStore.ipProjects.map((proj: any) => ({
		key: capitalizeFirstLetter(proj?.name ?? ""),
		value: proj?.id,
	}));

	const [searchValue, setSearchValue] = useState<string>("");
	const filteredItems = useMemo(() => {
		if (!searchValue) return items;
		const q = searchValue.toLowerCase();
		return items.filter((it) => (it.key || "").toLowerCase().includes(q));
	}, [items, searchValue]);

	const endStickyItem = (
		<div
			className={dropdownStyles.footerCta}
			onClick={() => {
				navigate(RouterConstants.IP_PROJECTS.fullPath);
			}}
		>
			<span className={dropdownStyles.footerText}>{t("ipProjects.createNew")}</span>
		</div>
	);

	const emptyStateItems =
		items.length === 0
			? [
					{
						key: "",
						value: "__empty_state__",
						disabled: true,
						className: "noDisabledOpacity",
						customItem: (
							<div className={dropdownStyles.emptyState}>
								<div className={dropdownStyles.emptyTitle}>{t("ipProjects.emptyTitle")}</div>
								<div className={dropdownStyles.emptyDesc}>{t("ipProjects.emptyDescription")}</div>
							</div>
						),
					},
				]
			: filteredItems;

	if (!isIpProjectsEnabled) return null;

	return (
		<Box className={orgStyles.orgDropdown}>
			<BriaDropdown
				icon={<IpProjectIcon />}
				selectedIcon={<IpProjectIcon />}
				addNoneOption={items.length > 0}
				loading={loading}
				hideArrowIcon={loading}
				value={value}
				items={emptyStateItems as any}
				endStickyItem={endStickyItem}
				menuWidth={items.length === 0 ? "260px" : undefined}
				searchable={true}
				searchValue={searchValue}
				onSearchChange={(e) => setSearchValue(e.target.value)}
				SearchPlaceholder={t("search")}
				searchIcon={true}
				// disabled={loading || items.length <= 0}
				onChange={changeHandler}
				width={"100%"}
				border={"none"}
				height={isAppsURL() ? "32px" : "40px"}
				labelFontWeight={600}
				labelFontSize={"14px"}
				placeholder={t("ipProjectsDropdownPlaceholder")}
				minHeight="auto !important"
				placeholderBold
			/>
		</Box>
	);
};

export default observer(IpProjectsDropdown);
